import React from 'react'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { jobTypeToString } from 'utils'
import { DutyItem_DutyFragment } from 'generated/graphql'
import Avatar from '@app.components/Avatar/Avatar'
import Button from '@app.components/Button'
import useDuplicateSelfIntroduction from '@app.feature/coverletter/coverletter.write/hook/useDuplicateSelfIntroduction'
import { formatRemainingTimeToString } from '@app.modules/util/time'
import MagnifyingGlassIcon from 'svgIcons/MagnifyingGlass'
import { useDutyModalStore } from '../../store/dutyModal'
import QuestionList from './QuestionList'

gql`
  fragment DutyItem_Duty on Duty {
    id
    title
    jobType
    selfIntroductionCount
    categories {
      id
      name
    }
    questionTemplates {
      id
      charSize
      charMaxSize
      blankType
      content
    }
    activity {
      id
      logoImage {
        id
        url
      }
      organizationName
      title
      recruitType
      recruitCloseAt
    }
    ...useDuplicateSelfIntroduction_Duty
  }
`

type TProps = {
  data: DutyItem_DutyFragment
  length: number
}

const DutyItem: React.FC<TProps> = ({ data, length }) => {
  const closeModal = useDutyModalStore((store) => store.closeModal)
  const duplicateSelfIntroduction = useDuplicateSelfIntroduction(
    data,
    closeModal,
  )

  const openRecruitPage = () => {
    if (!data.activity || !window) return
    window.open(`/activity/${data.activity.id}`)
  }

  const jobType = jobTypeToString(data.jobType) || '-'

  const createSelfIntroduction = async () => {
    await duplicateSelfIntroduction()
  }

  // TODO: 레거시 코드라 내부 코드 자체를 수정하게되면 사이드 이슈가 발생할 수 있어 플래그 추가방식으로 수정
  // 개선 필요합니다
  const remainingTime = formatRemainingTimeToString(
    data.activity?.recruitCloseAt || undefined,
    data.activity?.recruitType,
    'DutyItem',
    true,
  )

  const writeCount = `${data.selfIntroductionCount}명`

  return (
    <StyledWrapper>
      <div className="company-name-wrapper">
        <div className="company-image">
          <Avatar
            src={`${data.activity?.logoImage?.url}?d=autox34`}
            className="avatar"
          />
        </div>

        <div className="company-name">{data.activity?.organizationName}</div>
      </div>
      <a className="duty-name" onClick={openRecruitPage}>
        {data.title}
      </a>
      <div className="job-type">{jobType}</div>
      <div className="deadline">{remainingTime}</div>
      <div className="question" data-active={length < 3}>
        <div className="search-icon">
          <MagnifyingGlassIcon />
        </div>
        <QuestionList
          className="question-list"
          data={data.questionTemplates || []}
        />
      </div>
      <div className="write-count">{writeCount}</div>
      <Button
        className="create-button"
        variant="outlined"
        color="primary"
        onClick={createSelfIntroduction}
      >
        작성하기
      </Button>
    </StyledWrapper>
  )
}

export default DutyItem

const StyledWrapper = styled.div`
  border-bottom: 1px solid #eeeeee;
  display: flex;
  height: 60px;
  align-items: center;
  font-size: 14px;
  width: 100%;

  .company-name-wrapper {
    display: flex;
    align-items: center;
    margin-right: 10px;
    .company-image {
      width: 36px;
      height: 36px;
      margin-right: 10px;
    }
    .company-name {
      font-size: 12px;
      width: 119px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      font-weight: bold;
    }
  }

  .duty-name {
    color: #333333;
    width: 290px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    font-weight: 400;
    &:hover {
      text-decoration: underline;
    }
  }
  .deadline {
    width: 75px;
    margin-right: 0px;
    text-align: center;
  }
  .write-count {
    text-align: center;
    width: 60px;
    margin-right: 25px;
  }
  .job-type {
    width: 60px;
    text-align: center;
  }
  .question {
    width: 68px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    .search-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #eeeeee;
      svg {
        width: 13px;
        height: 13px;
      }
    }
    .question-list {
      display: none;
      position: absolute;
      z-index: 1;
      top: -10px;
      right: 100%;
      padding: 0 0px 0px 0;
    }
    &:hover {
      .search-icon {
        background-color: #f2faff;
        svg {
          path {
            fill: #01a0ff;
          }
        }
      }
      .question-list {
        display: block;
      }
    }
    &[data-active='true'] {
      .question-list {
        top: -10px;
        bottom: inherit;
      }
    }
  }
  .activity-link {
    font-size: 12px;
    display: flex;
    align-items: center;
    padding-right: 20px;
    .right-icon {
      display: flex;
      align-items: center;
      margin-top: 3px;
      width: 9px;
      height: 9px;
    }
  }

  &:nth-last-child(1),
  &:nth-last-child(2) {
    .question-list {
      top: inherit;
      bottom: -10px;
    }
  }
  &:hover {
    background-color: #fafafa;
  }

  .create-button {
    cursor: pointer;
    width: 82px;
    font-weight: 600;
    height: 40px;
    font-size: 12px;
    /* padding: 12px 20px; */
  }

  .avatar {
    border-radius: 5px;
    border: 1px solid #dddddd;
    & img {
      object-fit: contain;
      background-color: #fff;
    }
  }
`
